import Filter from '@/components/organism/Filter';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import useQueryValues from '@/hooks/useQueryValues';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import { AgGridReact } from 'ag-grid-react';
import Table from '@/components/atom/Table';
import SectionCardNoneTitle from '@/components/molecule/SectionCardNoneTitle';
import Typo from '@/components/atom/Typo';
import usePaginationQuery from '@/hooks/usePaginationQuery';
import { NAV_CONST } from '@/src/constants/nav';
import { useNavigate } from 'react-router-dom';
import { customersFilters } from '@/src/pages/Customer/NormalCustomer/filter';
import { normalCustomerListColumn } from '@/src/pages/Customer/NormalCustomer/column';
import {
  getCustomersAPI,
  getCustomersExcelAPI,
  getCustomersStatsAPI,
} from '@/src/apis/rest/customers';
import PATH from '@/src/routes/path';
import DashboardCard from '@/components/organism/DashboardCard';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';

function NormalCustomerPage() {
  const navigate = useNavigate();

  const { defaultValues: defaultSearchParams } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [searchParams, setSearchParams] = useState(defaultSearchParams);

  const {
    data: customersData,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['normal-customers', searchParams, page, pageSize],
    queryFn: () => {
      return getCustomersAPI({
        ...searchParams,
        type: 'normal',
        page,
        pageSize,
      });
    },
  });

  const { data: customerStatsData } = useQuery({
    queryKey: ['customers-stats', 'normal'],
    queryFn: () => getCustomersStatsAPI('normal'),
  });

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.normalCustomersManagement}
        breadcrumbList={[NAV_CONST.normalCustomersManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <DashboardCard
          data={[
            {
              key: '전체 회원',
              value: `${customerStatsData?.totalCount || 0}명`,
            },
            {
              key: '일반 회원',
              value: `${customerStatsData?.activatedCount || 0}명`,
            },
            {
              key: '정지 회원',
              value: `${customerStatsData?.inActivatedCount || 0}명`,
            },
            {
              key: '탈퇴 회원',
              value: `${customerStatsData?.withdrawalCount || 0}명`,
            },
          ]}
        />
        <Filter
          title="검색 필터"
          filters={customersFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setSearchParams(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">개인 회원 목록</Typo>
              <ExcelDownloadButton
                disabled={isFetching || isError || !customersData?.rows?.length}
                fileName="개인회원_리스트"
                downloadExcelAPI={getCustomersExcelAPI}
                searchParams={{ ...searchParams, type: 'normal' }}
              />
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : customersData?.rows || []}
              isPaginationDataMapping
              columnDefs={normalCustomerListColumn(e => {
                navigate(PATH.NORMAL_CUSTOMER_DETAIL.replace(':id', String(e)));
              })}
              total={customersData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>
    </>
  );
}

export default NormalCustomerPage;
